import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  ShieldCheck,
  ClipboardList,
  Calendar,
  Cpu,
  User,
  Clock,
  CheckCircle2,
  FileText,
  AlertTriangle,
  Flame,
  Tag,
  Search,
  X,
  Filter,
  Layers,
  Wrench,
  Loader2,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import { workOrdersAPI } from "../api";
import DocumentEvidenceList from "../components/DocumentEvidenceList";
import { formatIstDate, formatIstDateTime } from "../utils/date";

const statusConfig = {
  draft: {
    label: "Draft",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
  approved: {
    label: "Approved",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
  },
  in_progress: {
    label: "In Progress",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  completed: {
    label: "Completed",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
  },
  cancelled: {
    label: "Cancelled",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
};

const priorityConfig = {
  critical: {
    label: "Critical",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
  high: {
    label: "High",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  medium: {
    label: "Medium",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
  },
  low: {
    label: "Low",
    badge: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
};

const WorkOrderDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [docSearch, setDocSearch] = useState("");
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ["work-order", id],
    queryFn: () => workOrdersAPI.getById(id).then((response) => response.data.data),
  });

  const startMutation = useMutation({
    mutationFn: () => workOrdersAPI.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      toast.success("Work order started successfully");
    },
    onError: (err) => toast.error("Unable to start work order: " + (err.response?.data?.error || err.message)),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center h-96 items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500/20 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Loading Work Order Details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-12 text-center bg-white border border-slate-200/80 rounded-2xl max-w-lg mx-auto mt-12 shadow-xs">
        <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-800">Work Order Not Found</h3>
        <p className="text-xs text-slate-500 mt-1 mb-5">
          This work order could not be retrieved or has been removed.
        </p>
        <Link to="/work-orders" className="btn btn-secondary text-xs py-2 px-4">
          Back to Work Orders
        </Link>
      </div>
    );
  }

  const { workOrder, maintenanceLog, documents = [] } = data;
  const currentStatus = statusConfig[workOrder.status] || statusConfig.draft;
  const currentPriority = priorityConfig[workOrder.priority] || priorityConfig.medium;

  // Filter documents based on search
  const filteredDocuments = documents.filter((doc) => {
    if (!docSearch.trim()) return true;
    const query = docSearch.toLowerCase();
    const type = (doc.document_type || "").toLowerCase();
    const refNum = (doc.reference_number || "").toLowerCase();
    const reportNum = (doc.extracted_data?.report_number || "").toLowerCase();
    return type.includes(query) || refNum.includes(query) || reportNum.includes(query);
  });

  return (
    <div className="space-y-5 lg:space-y-6 w-full">
      {/* Top Header matching MaintenanceDetail & SOPViewer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-200/80">
        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
          <Link
            to="/work-orders"
            className="p-2 sm:p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-colors shrink-0"
            title="Back to work orders"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {workOrder.title}
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 shrink-0">
                <ClipboardList className="w-3 h-3" />
                {workOrder.work_order_number}
              </span>
              <span
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border shrink-0",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 truncate">
              <span className="flex items-center gap-1 font-medium text-slate-600">
                <Cpu className="w-3.5 h-3.5 text-slate-400" />
                {workOrder.machine_name || "General Machinery"}
              </span>
              <span className="text-slate-300">•</span>
              <span className="capitalize">{workOrder.work_order_type || "Standard"}</span>
              {workOrder.created_at && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>Created {formatIstDate(workOrder.created_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
          {maintenanceLog ? (
            <Link
              to={`/maintenance/${maintenanceLog.id}`}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-xs inline-flex items-center gap-1.5 font-medium"
            >
              <Wrench className="w-4 h-4 text-blue-600" />
              <span>View Maintenance Execution</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => startMutation.mutate()}
              disabled={startMutation.isPending}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-1.5 font-medium"
            >
              {startMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              <span>Start Work Execution</span>
            </button>
          )}
        </div>
      </div>

      {/* 4-Metric Summary Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Work Order Type
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800 capitalize">
            {workOrder.work_order_type || "Standard"}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Priority Level
          </span>
          <div className="mt-1">
            <span
              className={clsx(
                "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-semibold border",
                currentPriority.badge
              )}
            >
              <span className={clsx("w-1.5 h-1.5 rounded-full", currentPriority.dot)} />
              <span>{currentPriority.label}</span>
            </span>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Scheduled Start
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800">
            {workOrder.scheduled_start ? formatIstDateTime(workOrder.scheduled_start) : "Unscheduled"}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-200/80 rounded-2xl shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
            Evidence Documents
          </span>
          <p className="mt-1 text-sm sm:text-base font-bold text-slate-800">
            {documents.length} attached
          </p>
        </div>
      </div>

      {/* Main 12-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
        {/* Left Column (8 cols): Purpose & Scope, Quality Evidence */}
        <div className="lg:col-span-8 space-y-5 lg:space-y-6">
          {/* Work Order Description / Scope Card */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Work Order Directives & Scope
                  </h3>
                  <p className="text-xs text-slate-500">
                    Planned tasks, procedure grounding & objectives
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200/70 text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-medium">
                {workOrder.description || "No specific description provided for this work order."}
              </div>

              {workOrder.recommended_action && (
                <div className="p-4 bg-blue-50/60 border border-blue-100 rounded-xl space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    Recommended Action / Plan
                  </span>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {workOrder.recommended_action}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quality & Document Evidence Card with integrated Search Bar */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Quality / Document Evidence
                  </h3>
                  <p className="text-xs text-slate-500">
                    {documents.length} verified compliance records attached
                  </p>
                </div>
              </div>
            </div>

            {isEvidenceExpanded && (
              <div className="space-y-4">
                {/* Search Bar Section styled strictly like Maintenance.jsx */}
                {documents.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="relative flex-1 sm:max-w-md">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={docSearch}
                        onChange={(e) => setDocSearch(e.target.value)}
                        placeholder="Search evidence by report #, document type..."
                        className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
                      />
                      {docSearch && (
                        <button
                          type="button"
                          onClick={() => setDocSearch("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {docSearch && (
                      <button
                        type="button"
                        onClick={() => setDocSearch("")}
                        className="text-xs font-semibold text-slate-500 hover:text-rose-600 px-2.5 py-1.5 rounded-xl hover:bg-rose-50 transition-colors self-start sm:self-auto"
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                )}

                {/* Evidence Document List */}
                <DocumentEvidenceList documents={filteredDocuments} />
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar (4 cols): Metadata Details Card */}
        <div className="lg:col-span-4 space-y-5 lg:space-y-6">
          {/* Metadata Card matching MaintenanceDetail & SOPViewer */}
          <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-5 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Order Metadata
                </h3>
              </div>
              <span
                className={clsx(
                  "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                  currentStatus.badge
                )}
              >
                <span className={clsx("w-1.5 h-1.5 rounded-full", currentStatus.dot)} />
                <span>{currentStatus.label}</span>
              </span>
            </div>

            <div className="divide-y divide-slate-100 text-xs">
              {/* Machine Unit */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Cpu className="w-3.5 h-3.5 text-slate-400" /> Machine
                </span>
                <span className="font-semibold text-slate-800 truncate max-w-[160px]">
                  {workOrder.machine_name || "Unassigned"}
                </span>
              </div>

              {/* Procedure / SOP */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <FileText className="w-3.5 h-3.5 text-slate-400" /> Standard Procedure
                </span>
                <span className="font-semibold text-slate-800 truncate max-w-[160px]">
                  {workOrder.sop_title || "Standard Practice"}
                </span>
              </div>

              {/* Assigned Technician */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <User className="w-3.5 h-3.5 text-slate-400" /> Assigned To
                </span>
                <span className="font-semibold text-slate-800">
                  {workOrder.assigned_to_name || "Unassigned"}
                </span>
              </div>

              {/* Requested By */}
              {workOrder.requested_by_name && (
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <User className="w-3.5 h-3.5 text-slate-400" /> Requested By
                  </span>
                  <span className="font-semibold text-slate-800">
                    {workOrder.requested_by_name}
                  </span>
                </div>
              )}

              {/* Scheduled Start */}
              <div className="flex justify-between items-center py-2.5 gap-2">
                <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" /> Scheduled Start
                </span>
                <span className="font-semibold text-slate-800">
                  {workOrder.scheduled_start ? formatIstDateTime(workOrder.scheduled_start) : "Not set"}
                </span>
              </div>

              {/* Scheduled End */}
              {workOrder.scheduled_end && (
                <div className="flex justify-between items-center py-2.5 gap-2">
                  <span className="text-slate-500 font-medium flex items-center gap-1.5 shrink-0">
                    <Clock className="w-3.5 h-3.5 text-slate-400" /> Scheduled End
                  </span>
                  <span className="font-semibold text-slate-800">
                    {formatIstDateTime(workOrder.scheduled_end)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetail;
